import React, { useState, useEffect, useRef } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardIcon from "@mui/icons-material/Dashboard";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getUser } from "../../utils/auth";
import { menuAPI, privilegeAPI } from "../../services/api";

// Dynamic Icon Component that supports Font Awesome classes and Material-UI icons
const DynamicIcon = ({ iconClass, active, menuCode }) => {
  const iconClasses = `sidebar-menu-icon ${active ? 'sidebar-menu-icon-active' : ''} ${menuCode ? `sidebar-menu-icon-${menuCode.toLowerCase().replace(/\s+/g, '-')}` : ''}`;
  
  // Check if it's a Font Awesome class (starts with "fa " or "fas " or "far " or "fab " or "fal ")
  if (iconClass && (iconClass.startsWith("fa ") || iconClass.startsWith("fas ") || iconClass.startsWith("far ") || iconClass.startsWith("fab ") || iconClass.startsWith("fal "))) {
    return (
      <i
        className={`${iconClass} ${iconClasses}`}
        style={{
          color: active ? "#1976d2" : "#666666",
          fontSize: "17px",
        }}
      />
    );
  }

  // Fallback to Material-UI Dashboard icon if no icon class provided
  const IconComponent = DashboardIcon;
  return (
    <IconComponent
      className={iconClasses}
      sx={{
        color: active ? "#1976d2" : "#666666",
        fontSize: "17px",
      }}
    />
  );
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openMenus, setOpenMenus] = useState({});
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const menuLoadSeqRef = useRef(0);

  // Load menus based on user from USERMASTER.
  useEffect(() => {
    const seq = ++menuLoadSeqRef.current;

    const loadMenus = async () => {
      try {
        setLoading(true);
        const user = getUser();

        // Get userIdCode from user object
        // If userIdCode doesn't exist, try to use UserName as fallback
        // (assuming UserName maps to USERIDCODE in USERMASTER)
        let userIdCode =
          user?.UserIdCode ||
          user?.userIdCode ||
          user?.UserName ||
          user?.userName;

        if (!userIdCode) {
          console.warn("UserIdCode not found in user object");
          if (seq === menuLoadSeqRef.current) setMenuItems([]);
          return;
        }

        const response = await menuAPI.getUserMenus(userIdCode);
        if (seq !== menuLoadSeqRef.current) return;

        // ApiResponse<List<...>> or a bare array from older clients.
        const menuList = Array.isArray(response)
          ? response
          : response?.data ?? response?.Data;
        if (Array.isArray(menuList)) {
          setMenuItems(menuList);
          try {
            // Persist menu list so permission checks can resolve MenuCode by PageName dynamically.
            localStorage.setItem("csrp.userMenus", JSON.stringify(menuList));
          } catch {
            // ignore storage errors
          }

          // Also cache privilege details with permission flags (VIEW/ADD/EDIT/DELETE/DOWNLOAD).
          // Some login payloads do not include PrivilegeDetails, so we bootstrap it here.
          try {
            const privResp = await privilegeAPI.getAll({
              userIdCode: userIdCode,
              pageNumber: 1,
              pageSize: 5000,
            });
            const list =
              privResp?.data?.privileges ||
              privResp?.data?.PrivilegeDetails ||
              privResp?.data?.privilegeDetails ||
              privResp?.data?.data ||
              privResp?.data ||
              [];
            if (seq === menuLoadSeqRef.current && Array.isArray(list)) {
              localStorage.setItem("csrp.privilegeDetails", JSON.stringify(list));
            }
          } catch {
            // ignore; pages will default to allow-all when no MenuCode match
          }
        } else {
          console.warn(
            "Menu API did not return an array. Response:",
            response
          );
          setMenuItems([]);
        }
      } catch (error) {
        if (seq !== menuLoadSeqRef.current) return;
        console.error("Error loading menus:", error);
        setMenuItems([]);
      } finally {
        if (seq === menuLoadSeqRef.current) setLoading(false);
      }
    };

    loadMenus();
    return () => {
      menuLoadSeqRef.current += 1;
    };
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleMenuClick = (menuCode) => {
    setOpenMenus((prev) => ({
      ...prev,
      [menuCode]: !prev[menuCode],
    }));
  };

  const renderMenuItem = (menu, level = 0) => {
    // Handle both PascalCase and camelCase property names
    const menuCode = menu.MenuCode || menu.menuCode;
    const menuName = menu.MenuName || menu.menuName;
    const pageName = menu.PageName || menu.pageName;
    const menuIcon = menu.MenuIcon || menu.menuIcon;
    const classIcon = menu.ClassIcon || menu.classIcon;
    const children = menu.Children || menu.children;

    // Use MenuIcon from database (supports Font Awesome classes like "fa fa-users")
    const iconClass = menuIcon || classIcon || "";
    const hasChildren =
      children && Array.isArray(children) && children.length > 0;
    const isOpen = openMenus[menuCode] || false;
    const active = isActive(pageName);
    const isSubmenu = level > 0;

    // Sort children by DisplayOrder if they exist
    const sortedChildren = hasChildren
      ? [...children].sort((a, b) => {
          const orderA = a.DisplayOrder ?? a.displayOrder ?? 0;
          const orderB = b.DisplayOrder ?? b.displayOrder ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          const codeA = a.MenuCode ?? a.menuCode ?? "";
          const codeB = b.MenuCode ?? b.menuCode ?? "";
          return codeA.localeCompare(codeB);
        })
      : null;

    if (hasChildren) {
      const menuItemClass = `sidebar-menu-item sidebar-menu-item-parent ${active ? 'sidebar-menu-item-active' : ''} ${isSubmenu ? 'sidebar-submenu-item' : ''} ${menuCode ? `sidebar-menu-item-${menuCode.toLowerCase().replace(/\s+/g, '-')}` : ''}`;
      
      return (
        <React.Fragment key={menuCode}>
          <ListItem disablePadding className={menuItemClass}>
            <ListItemButton
              className={`${menuItemClass}-button`}
              onClick={() => handleMenuClick(menuCode)}
              sx={{
                backgroundColor: active ? "#f5f5f5" : "transparent",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
                pl: isSubmenu ? "33px" : 2,
              }}
            >
              <ListItemIcon className={`sidebar-menu-icon-wrapper ${menuCode ? `sidebar-menu-icon-wrapper-${menuCode.toLowerCase().replace(/\s+/g, '-')}` : ''}`} sx={{ minWidth: "30px" }}>
                <DynamicIcon iconClass={iconClass} active={active} menuCode={menuCode} />
              </ListItemIcon>
              <ListItemText
                className={`sidebar-menu-text ${menuCode ? `sidebar-menu-text-${menuCode.toLowerCase().replace(/\s+/g, '-')}` : ''}`}
                primary={menuName || menuCode}
                sx={{
                  "& .MuiListItemText-primary": {
                    color: active ? "#1976d2" : "#333333",
                    fontWeight: active ? 600 : 400,
                    fontSize: "14px",
                  },
                }}
              />
              <KeyboardArrowDownIcon
                className={`sidebar-menu-arrow ${isOpen ? 'sidebar-menu-arrow-open' : ''} ${menuCode ? `sidebar-menu-arrow-${menuCode.toLowerCase().replace(/\s+/g, '-')}` : ''}`}
                sx={{
                  color: active ? "#1976d2" : "#666666",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease-in-out",
                  ml: 1,
                }}
              />
            </ListItemButton>
          </ListItem>
          {isOpen && sortedChildren && sortedChildren.map((child) => renderMenuItem(child, level + 1))}
        </React.Fragment>
      );
    }

    const menuItemClass = `sidebar-menu-item ${active ? 'sidebar-menu-item-active' : ''} ${isSubmenu ? 'sidebar-submenu-item' : ''} ${menuCode ? `sidebar-menu-item-${menuCode.toLowerCase().replace(/\s+/g, '-')}` : ''}`;
    
    return (
      <ListItem key={menuCode} disablePadding className={menuItemClass}>
        <ListItemButton
          className={`${menuItemClass}-button`}
          onClick={() => navigate(pageName)}
          sx={{
            backgroundColor: active ? "#f5f5f5" : "transparent",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
            pl: isSubmenu ? "33px" : 2,
          }}
        >
          <ListItemIcon className={`sidebar-menu-icon-wrapper ${menuCode ? `sidebar-menu-icon-wrapper-${menuCode.toLowerCase().replace(/\s+/g, '-')}` : ''}`} sx={{ minWidth: "30px" }}>
            <DynamicIcon iconClass={iconClass} active={active} menuCode={menuCode} />
          </ListItemIcon>
          <ListItemText
            className={`sidebar-menu-text ${menuCode ? `sidebar-menu-text-${menuCode.toLowerCase().replace(/\s+/g, '-')}` : ''}`}
            primary={menuName || menuCode}
            sx={{
              "& .MuiListItemText-primary": {
                color: active ? "#1976d2" : "#333333",
                fontWeight: active ? 600 : 400,
                fontSize: "14px",
              },
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#ffffff",
      }}
    >
      <List sx={{ pt: 2, flexGrow: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : menuItems.length === 0 ? (
          <Typography variant="body2" sx={{ p: 2, color: "#666666" }}>
            No menus available
          </Typography>
        ) : (
          menuItems
            .sort((a, b) => {
              const orderA = a.DisplayOrder ?? a.displayOrder ?? 0;
              const orderB = b.DisplayOrder ?? b.displayOrder ?? 0;
              if (orderA !== orderB) return orderA - orderB;
              const codeA = a.MenuCode ?? a.menuCode ?? "";
              const codeB = b.MenuCode ?? b.menuCode ?? "";
              return codeA.localeCompare(codeB);
            })
            .map((menu) => renderMenuItem(menu))
        )}
      </List>
    </Box>
  );
};

export default Sidebar;
